import DropDown from "../../shared/DropDown";
import bridge from "../../../turbo/bridge";
import {
  faComment,
  faCommentSlash,
  faLock,
  faMailbox,
  faStar as faStarSolid,
  faThumbTack,
  faThumbTackSlash,
  faUser,
  faFlag,
} from "@fortawesome/pro-solid-svg-icons";
import { useState } from "react";
import ChangeAuthorForm from "./contextMenuForms/ChangeAuthorForm.tsx";
import { every, filter, get, map, some } from "lodash";
import {
  faBoxArchive,
  faBoxOpen,
  faClone,
  faEnvelope,
  faPencil,
  faMailbox as faMailboxRegular,
  faPrint,
  faCheckSquare,
  faSquare,
  faFileCsv,
  faFileExcel,
  faStar as faStarRegular,
  faShareSquare,
  faBullhorn,
  faEyeSlash,
  faTrash,
} from "@fortawesome/pro-regular-svg-icons";
import EmailRecommendationForm from "./contextMenuForms/EmailRecommendationForm.tsx";
import { Activitystream } from "../../../api/activitystreams.ts";
import { Activity, ActivityObject } from "../../../@types/activities.ts";
import EditActivityForm from "./contextMenuForms/EditActivityForm.tsx";
import { useUpdateActivityInCache } from "../../../hooks/activitystreams/useUpdateActivityInCache.ts";
import {
  useCloseSurvey,
  useDestroyActivity,
  useIgnoreAuthor,
  useSetSticky,
  useToggleArchived,
  useToggleBookmark,
  useToggleNewsletterActivity,
  useToggleRecommendation,
  useToggleRepliesClosed,
  useToggleSubscriptionEmail,
} from "./api.ts";
import queryClient from "../../../helpers/queryClient.tsx";

type ActivityContextMenuArgs = {
  activityId: string;
  emailRecommendationCount?: { internal: number; external: number };
  emailRecommendationInfo?: {
    hasAttachments: boolean;
    totalAttachmentSize: number;
    lastRecommendation?: Activity["email_recommendation"];
  };
  activityCan: Activity["can"];
  streamUrl?: string;
  teaser?: string;
  streamsFromActivity?: Activitystream[];
  repliesClosed?: Activity["replies_closed"];
  plannedForNewsletterAt?: Activity["planned_for_newsletter_at"];
  canShare?: boolean;
  shareActivity?: () => void;
  objectId?: string;
  sticky?: Activity["sticky"];
  archivedAt?: Activity["archived_at"];
  iSubscribedEmail?: Activity["i_subscribed_email"];
  actor: Activity["actor"];
  iBookmarked?: ActivityObject["i_bookmarked?"];
  iRecommend?: ActivityObject["i_recommend?"];
  closable?: boolean;
  closed?: boolean;
} & Omit<ActivityObject, "id" | "i_bookmarked?" | "i_recommend?">;

export default function ActivityContextMenu({
  activityId,
  streamsFromActivity,
  emailRecommendationCount,
  emailRecommendationInfo,
  teaser,
  activityCan,
  type,
  objectId,
  repliesClosed,
  plannedForNewsletterAt,
  iSubscribedEmail,
  sticky,
  archivedAt,
  streamUrl,
  can,
  closable,
  closed,
  bookmarkable,
  shareActivity,
  iBookmarked,
  canShare,
  recommendable,
  iRecommend,
  actor,
}: ActivityContextMenuArgs) {
  const [showChangeAuthorModal, setShowChangeAuthorModal] = useState(false);
  const [showEditActivityModal, setShowEditActivityModal] = useState(false);
  const [showEmailRecommendationModal, setShowEmailRecommendationModal] =
    useState<"internal" | "external" | undefined>(undefined);

  const { updateActivity, removeActivity } = useUpdateActivityInCache();

  const { mutate: toggleRepliesClosed } = useToggleRepliesClosed(
    activityId,
    repliesClosed,
    {
      onSuccess: ({ replies_closed }: { replies_closed: boolean }) =>
        updateActivity(activityId, (activity) => ({
          ...activity,
          replies_closed,
        })),
    },
  );

  const { mutate: toggleNewsletterActivity } = useToggleNewsletterActivity(
    activityId,
    plannedForNewsletterAt,
    {
      onSuccess: () =>
        updateActivity(
          activityId,
          (activity) =>
            ({
              ...activity,
              planned_for_newsletter_at: plannedForNewsletterAt
                ? null
                : new Date().toISOString(),
            }) as Activity,
        ),
    },
  );

  const { mutate: setSticky } = useSetSticky(activityId, {
    onSuccess: (_data, sticky) => {
      streamUrl &&
        queryClient.invalidateQueries(`${streamUrl}/sticky/activities`);
      updateActivity(
        activityId,
        (activity) =>
          ({
            ...activity,
            sticky: sticky === false ? null : sticky,
          }) as Activity,
      );
    },
  });

  const { mutate: toggleSubscription } = useToggleSubscriptionEmail(
    activityId,
    !!iSubscribedEmail,
    {
      onSuccess: () => {
        updateActivity(activityId, (activity) => ({
          ...activity,
          i_subscribed_email: !iSubscribedEmail,
        }));
      },
    },
  );

  const { mutate: closeSurvey } = useCloseSurvey(activityId, type, {
    onSuccess: () => {
      updateActivity(activityId, (activity) => ({
        ...activity,
        object: { ...activity.object, ["closed"]: true },
      }));
    },
  });

  const { mutate: toggleBookmark } = useToggleBookmark(
    activityId,
    !!iBookmarked,
    {
      onSuccess: () => {
        updateActivity(activityId, (activity) => ({
          ...activity,
          object: { ...activity.object, ["i_bookmarked?"]: !iBookmarked },
        }));
      },
    },
  );

  const { mutate: toggleRecommendation } = useToggleRecommendation(
    activityId,
    !!iRecommend,
    {
      onSuccess: () => {
        updateActivity(activityId, (activity) => ({
          ...activity,
          object: {
            ...activity.object,
            ["i_recommend?"]: !iRecommend,
            recommendation_count: iRecommend
              ? (activity.object.recommendation_count || 1) - 1
              : (activity.object.recommendation_count || 0) + 1,
          },
        }));
      },
    },
  );

  const { mutate: ignoreAuthor } = useIgnoreAuthor(actor.id, {
    onSuccess: () => {
      removeActivity(activityId);

      toastr.success(
        I18n.t("js.activitystreams.context_menu.ignore_author.success", {
          actor_name: actor.displayName,
        }),
      );
    },
  });

  const { mutate: destroyActivity } = useDestroyActivity(activityId, {
    onSuccess: () => {
      removeActivity(activityId);
    },
  });

  const { mutate: toggleArchived } = useToggleArchived(
    activityId,
    !!archivedAt,
    {
      onSuccess: (data) => {
        // The activity leaves the list the member is looking at either way:
        // archiving hides it from the stream, unarchiving removes it from the
        // "archived" filter. The detail-page cache keeps the updated fields.
        removeActivity(activityId);
        updateActivity(activityId, (activity) => ({
          ...activity,
          archived_at: (data as Activity).archived_at,
          archived_by_name: (data as Activity).archived_by_name,
        }));
        toastr.success(
          I18n.t(
            archivedAt
              ? "js.activity.successfully_unarchived"
              : "js.activity.successfully_archived",
          ),
        );
      },
    },
  );

  function checkNewsletterConditions(onProceed: () => void) {
    if (plannedForNewsletterAt) {
      onProceed();
      return;
    }

    if (every(streamsFromActivity, (s) => s.hidden)) {
      bridge.confirm(
        I18n.t(
          "js.activitystreams.newsletter.add_to_newsletter_hidden_confirmation",
        ),
        onProceed,
      );
      return;
    }

    if (every(streamsFromActivity, (s) => s.closed)) {
      bridge.confirm(
        I18n.t(
          "js.activitystreams.newsletter.add_to_newsletter_closed_confirmation",
        ),
        onProceed,
      );
      return;
    }

    onProceed();
  }

  function onToggleNewsletterActivity() {
    checkNewsletterConditions(toggleNewsletterActivity);
  }

  return (
    <div className="activity-context-menu context-menu-wrap">
      <DropDown.Wrapper>
        <DropDown.Toggle
          className="context-menu"
          buttonType="normal"
          iconSize={window.isApp ? "xl" : undefined}
          small
        />
        <DropDown>
          <DropDown.Item
            icon={faClone}
            text={I18n.t("js.activitystreams.permalink_copy_link")}
            onSelect={() => {
              navigator.clipboard
                .writeText(
                  `https://${Preload.current_network.host}/activities/${activityId}`,
                )
                .then(() =>
                  toastr.success(
                    I18n.t("js.activitystreams.directlink_copied"),
                  ),
                );
            }}
            dividerBottom
          />
          {activityCan.update_object && (
            <DropDown.Item
              text={I18n.t(
                type === "appointment"
                  ? "js.calendars.appointment.edit"
                  : "js.activitystreams.edit",
              )}
              icon={faPencil}
              onSelect={
                type === "appointment"
                  ? undefined
                  : () =>
                      window.isApp
                        ? bridge.navigateInApp(`/activities/${activityId}/edit`)
                        : setShowEditActivityModal(true)
              }
              url={
                type === "appointment"
                  ? `/appointments/${objectId}/edit`
                  : undefined
              }
            />
          )}
          {activityCan.update_activitystreams && type === "appointment" && (
            <DropDown.Item
              text={I18n.t("js.activitystreams.edit")}
              icon={faPencil}
              onSelect={() =>
                !window.isApp
                  ? setShowEditActivityModal(true)
                  : bridge.navigateInApp(`/activities/${activityId}/edit`)
              }
            />
          )}
          {activityCan.update_actor && (
            <DropDown.Item
              text={I18n.t("js.activitystreams.change_author.title")}
              icon={faUser}
              onSelect={() => setShowChangeAuthorModal(true)}
            />
          )}
          {(activityCan.update_actor ||
            activityCan.update_activitystreams ||
            activityCan.update_object) && <li className="divider" />}
          {map(
            ["internal", "external"],
            (type: "internal" | "external") =>
              some(
                streamsFromActivity,
                (stream) =>
                  stream.can &&
                  stream.can[`email_recommend_activity_${type}ly`],
              ) && (
                <DropDown.Item
                  key={type}
                  text={I18n.t(
                    `js.activitystreams.email_recommend_activity_${type}ly`,
                  )}
                  icon={faEnvelope}
                  onSelect={() => setShowEmailRecommendationModal(type)}
                  additional={
                    emailRecommendationCount &&
                    emailRecommendationCount[type] > 0 && (
                      <span className="text-muted pl-2">
                        {emailRecommendationCount[type]}
                      </span>
                    )
                  }
                />
              ),
          )}
          {activityCan.close_replies && (
            <DropDown.Item
              icon={repliesClosed ? faComment : faCommentSlash}
              text={I18n.t(
                repliesClosed
                  ? "js.activitystreams.comments.enable"
                  : "js.activitystreams.comments.disable",
              )}
              onSelect={() => toggleRepliesClosed()}
              dividerTop={
                activityCan.update_actor ||
                activityCan.update_activitystreams ||
                activityCan.update_object
              }
              dividerBottom
            />
          )}
          {activityCan.add_to_newsletter &&
            (!streamsFromActivity?.length ||
              !every(streamsFromActivity, (s) => s.sensitive)) && (
              <DropDown.Item
                icon={plannedForNewsletterAt ? faMailbox : faMailboxRegular}
                text={I18n.t(
                  plannedForNewsletterAt
                    ? "js.activitystreams.newsletter.remove_from_newsletter"
                    : "js.activitystreams.newsletter.add_to_newsletter",
                )}
                onSelect={() => onToggleNewsletterActivity()}
              />
            )}
          <DropDown.Item
            icon={faPrint}
            text={I18n.t("activitystreams.print")}
            {...(window.location.pathname.endsWith(`/activities/${activityId}`)
              ? // On the activity detail page the page IS this single activity,
                // so print directly (deferred past the dropdown-close re-render
                // so the menu isn't captured in the print output).
                {
                  onSelect: () =>
                    requestAnimationFrame(() =>
                      requestAnimationFrame(() => window.print()),
                    ),
                }
              : // Elsewhere (e.g. the stream/Verlauf) printing the current page
                // would print the whole list — open the activity's detail page
                // with #print first; ActivityPage auto-prints on load.
                window.isApp
                ? {
                    onSelect: () =>
                      bridge.navigateInApp(`/activities/${activityId}#print`),
                  }
                : { url: `/activities/${activityId}#print` })}
          />
          {activityCan.update_sticky &&
            (sticky ? (
              <DropDown.Item
                icon={faThumbTackSlash}
                text={I18n.t("activitystreams.sticky.reset")}
                onSelect={() => setSticky(false)}
              />
            ) : (
              <>
                {activityCan.set_regular_sticky && (
                  <DropDown.Item
                    icon={faThumbTack}
                    text={`${I18n.t("activitystreams.sticky.set")}: ${I18n.t(
                      "activitystreams.sticky.regular",
                    )}`}
                    onSelect={() => setSticky("regular")}
                  />
                )}
                <DropDown.Item
                  icon={faThumbTack}
                  text={`${I18n.t("activitystreams.sticky.set")}: ${I18n.t(
                    "activitystreams.sticky.confirmable",
                  )}`}
                  onSelect={() => setSticky("confirmable")}
                />
              </>
            ))}
          <DropDown.Item
            icon={iSubscribedEmail ? faCheckSquare : faSquare}
            text={I18n.t("activitystreams.subscribed_email")}
            onSelect={() => toggleSubscription()}
            dividerTop
          />
          {type === "survey" && activityCan.export_survey_results && (
            <>
              {map(["csv", "xlsx"], (exportType) => (
                <DropDown.Item
                  key={exportType}
                  text={I18n.t(`activitystreams.survey.export.${exportType}`)}
                  icon={exportType === "csv" ? faFileCsv : faFileExcel}
                  url={`/activities/${activityId}/survey_results.${exportType}`}
                  dividerTop={exportType === "csv"}
                />
              ))}
            </>
          )}
          {can?.close && closable && !closed && (
            <DropDown.Item
              icon={faLock}
              text={I18n.t(`js.activitystreams.close_${type}`)}
              onSelect={() => closeSurvey()}
              dividerTop
            />
          )}
          {can?.bookmark && bookmarkable && (
            <DropDown.Item
              text={I18n.t(
                `js.activitystreams.${iBookmarked ? "unbookmark" : "bookmark"}`,
              )}
              icon={iBookmarked ? faStarSolid : faStarRegular}
              onSelect={() => toggleBookmark()}
              dividerTop
            />
          )}
          {canShare && (
            <DropDown.Item
              text={I18n.t("activitystreams.share")}
              icon={faShareSquare}
              onSelect={() => shareActivity?.()}
            />
          )}
          {can?.recommend && recommendable && (
            <DropDown.Item
              icon={faFlag}
              text={I18n.t(
                `js.activitystreams.${iRecommend ? "unrecommend" : "recommend"}`,
              )}
              onSelect={() => toggleRecommendation()}
            />
          )}
          {activityCan.report && (
            <DropDown.Item
              text={I18n.t("js.activitystreams.report")}
              icon={faBullhorn}
              url={`/activities/${activityId}/report/new`}
              dividerTop
            />
          )}
          {Preload.current_member?.id &&
            actor.id !== Preload.current_member.id &&
            actor.type !== "blog" && (
              <DropDown.Item
                text={I18n.t("js.activitystreams.ignore", {
                  name: actor.displayName,
                })}
                icon={faEyeSlash}
                onSelect={() =>
                  bridge.confirm(
                    I18n.t(
                      "js.activitystreams.context_menu.ignore_author.confirmation",
                      { actor_name: actor.displayName },
                    ),
                    () => ignoreAuthor(),
                  )
                }
                dividerTop
              />
            )}
          {activityCan.archive && (
            <DropDown.Item
              text={I18n.t(
                archivedAt
                  ? "js.activitystreams.unarchive"
                  : "js.activitystreams.archive",
              )}
              icon={archivedAt ? faBoxOpen : faBoxArchive}
              onSelect={() =>
                archivedAt
                  ? toggleArchived()
                  : bridge.confirm(
                      I18n.t("js.activity.archive_confirmation"),
                      () => toggleArchived(),
                    )
              }
              dividerTop
            />
          )}
          {activityCan.destroy && (
            <DropDown.Item
              text={I18n.t("js.activitystreams.delete")}
              icon={faTrash}
              onSelect={() =>
                bridge.confirm(I18n.t("js.activity.delete_confirmation"), () =>
                  destroyActivity(),
                )
              }
              dividerTop={!activityCan.archive}
            />
          )}
        </DropDown>
      </DropDown.Wrapper>
      {showEditActivityModal && (
        <EditActivityForm
          activityId={activityId}
          close={() => setShowEditActivityModal(false)}
        />
      )}
      {showChangeAuthorModal && (
        <ChangeAuthorForm
          activityId={activityId}
          close={() => setShowChangeAuthorModal(false)}
        />
      )}
      {showEmailRecommendationModal &&
        emailRecommendationInfo &&
        emailRecommendationCount && (
          <EmailRecommendationForm
            activityId={activityId}
            type={showEmailRecommendationModal}
            emailRecommendationInfo={emailRecommendationInfo}
            emailRecommendationCount={emailRecommendationCount}
            teaser={teaser}
            selectableStreams={
              filter(streamsFromActivity, (stream: Activitystream) =>
                get(stream, [
                  "can",
                  `email_recommend_activity_${showEmailRecommendationModal}ly`,
                ]),
              ) as Activitystream[]
            }
            close={() => setShowEmailRecommendationModal(undefined)}
          />
        )}
    </div>
  );
}
